<script setup lang="ts">
import { ref } from 'vue'
import iconPicture from '~/assets/images/logo.png'
import type { ProfileRoleBadgeItem } from '~/types/profile'
import ProfilePopover from '~/components/app/ProfilePopover.vue'
import AppBrandEnvBadges from '~/components/app/layout/AppBrandEnvBadges.vue'
import SourceTag from '~/components/ui/SourceTag.vue'
import SourceButton from '~/components/ui/SourceButton.vue'
import GlobalSearchBar from '~/components/search/GlobalSearchBar.vue'
import { useEnedisSitesMapModal } from '~/composables/sites/useEnedisSitesMapModal'
import {
  ASYNC_OVERLAY_DELAY_MS,
  defineAsyncWithSkeleton,
} from '~/utils/defineAsyncWithSkeleton'

const EnedisSitesMapModal = defineAsyncWithSkeleton(
  function loadEnedisSitesMapModal() {
    return import('~/components/sites/EnedisSitesMapModal.client.vue')
  },
  undefined,
  ASYNC_OVERLAY_DELAY_MS,
)

withDefaults(
  defineProps<{
    loggedIn: boolean
    canEditOrganigramOnCurrentView: boolean
    isEditing: boolean
    editModeShortcutDeactivateHint: string
    fullName: string
    userEmail: string
    myNni?: string
    myAvatarDisplayMode: 'teams' | 'initials' | 'avatar'
    myAvatarConfig: Record<string, unknown> | null
    myGraphPhotoUrl: string | null
    profileRoleBadges: ProfileRoleBadgeItem[]
    departmentAttachment: string
    firstLoginDisplayFrench: string
    profileRightsLabels: string[]
    userCanAccessAdmin: boolean
    directionAdminDirLibs?: string[]
  }>(),
  {
    myNni: undefined,
    directionAdminDirLibs: () => [],
  },
)

const globalSearchBarRef = ref<InstanceType<typeof GlobalSearchBar> | null>(null)
const { isOpen: showEnedisSitesMap, open: openEnedisSitesMap } = useEnedisSitesMapModal()

defineExpose({
  globalSearchBarRef,
})
</script>

<template>
  <header
    data-testid="app-layout-header"
    :data-me-nni="myNni ?? ''"
    :data-admin-dirs="directionAdminDirLibs.join(',')"
  >
    <NuxtLink
      class="website-name"
      to="/"
    >
      <img :src="iconPicture" alt="mon-orga-icon">
      <h3 class="website-name__title h3">MonOrga</h3>
      <AppBrandEnvBadges />
    </NuxtLink>
    <div v-if="loggedIn" class="header-global-search-group">
      <GlobalSearchBar ref="globalSearchBarRef" class="header-global-search" />
      <SourceButton
        class="header-enedis-sites-button"
        type="secondary"
        size="small"
        icon="map"
        text="Site Enedis"
        @click="openEnedisSitesMap"
      />
    </div>
    <EnedisSitesMapModal v-if="loggedIn && showEnedisSitesMap" v-model="showEnedisSitesMap" />
    <div class="user">
      <div class="header-orga-tags">
        <div
          v-if="loggedIn && canEditOrganigramOnCurrentView && !isEditing"
          class="header-editable-hint"
          data-onboarding="header-editable-hint"
          role="status"
        >
          <SourceTag
            text="Éditable"
            icon="edit_note"
            icon-position="left"
            size="small"
            type="neutral"
            theme="light"
            flat
            hover-text="Vous pouvez activer le mode édition sur cette page."
          />
        </div>
        <div
          v-if="isEditing"
          class="header-editing-tag"
          data-onboarding="header-editing-tag"
          role="status"
        >
          <span class="visually-hidden">Mode édition activé. {{ editModeShortcutDeactivateHint }}</span>
          <SourceTag
            text="Mode édition"
            icon="edit"
            icon-position="left"
            size="small"
            type="warning"
            theme="light"
            flat
            :hover-text="editModeShortcutDeactivateHint"
          />
        </div>
      </div>
      <ProfilePopover
        v-if="loggedIn"
        :full-name="fullName"
        :email="userEmail"
        :nni="myNni"
        :avatar-display-mode="myAvatarDisplayMode"
        :avatar-config="myAvatarConfig"
        :photo-url="myGraphPhotoUrl"
        :role-badges="profileRoleBadges"
        :attachment="departmentAttachment"
        :first-login-display="firstLoginDisplayFrench"
        :rights="profileRightsLabels"
        :can-access-admin="userCanAccessAdmin"
      />
    </div>
  </header>
</template>
